import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '@/api/client';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  tenant_id: string;
  tenant_name: string;
  is_sso_user: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: RegisterData) => Promise<void>;
  exchangeSSO: (token: string, patientId?: string) => Promise<string | null>;
  refreshAuth: () => Promise<void>;
  setUser: (user: User) => void;
  setAuth: (accessToken: string, refreshToken: string, user: User) => void;
  clearError: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  organization_name: string;
  organization_phone?: string;
  accept_terms: boolean;
  accept_privacy: boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post('/auth/login', {
            email,
            password,
          });

          const { access_token, refresh_token, user } = response.data;

          // Set token in API client
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          const message = error.response?.data?.error?.message || 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        // Clear API client auth header
        delete apiClient.defaults.headers.common['Authorization'];

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          await apiClient.post('/auth/register', data);
          set({ isLoading: false });
        } catch (error: any) {
          const message = error.response?.data?.error?.message || 'Registration failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      exchangeSSO: async (token: string, patientId?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.post('/auth/sso/exchange', {
            sso_token: token,
            patient_id: patientId,
          });

          const { access_token, refresh_token, user, patient_id: returnedPatientId } = response.data;

          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });

          return returnedPatientId;
        } catch (error: any) {
          const message = error.response?.data?.error?.message || 'SSO authentication failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      refreshAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          const response = await apiClient.post('/auth/refresh', {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;

          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          set({
            accessToken: access_token,
            refreshToken: newRefreshToken,
          });
        } catch (error) {
          get().logout();
        }
      },

      setUser: (user: User) => set({ user }),
      setAuth: (accessToken: string, refreshToken: string, user: User) => {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'perio-chart-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth header on app load
const token = useAuthStore.getState().accessToken;
if (token) {
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Listen for store rehydration and set header if needed
useAuthStore.subscribe((state) => {
  if (state.accessToken) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${state.accessToken}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
});
